import { gsap } from 'gsap'
import { optional, prefersReducedMotion, qsa } from '@/lib/dom'

/**
 * メインビジュアル（.js-mainVisual-init）の登場演出。
 * ローディング解除後に一度だけ再生する。
 *
 * 呼び出し時点では「非表示の初期状態」だけを適用した一時停止の
 * タイムラインを組み、返した関数が呼ばれてから再生する。
 * ローディングのフェードが始まる前に初期状態を敷いておくことで、
 * 生のページ → 消える → 再登場、という二重描画を防ぐ。
 *
 * 下から少しせり上げて定位置（y: 0）に着地させる。
 * 終了状態を必ず y: 0 にしておかないと、演出をスキップした場合に
 * FV が画面外へずれたまま残るため、reduced-motion でも set で揃える。
 */
export function initMainVisual(): () => void {
  const target = optional('.js-mainVisual-init')
  const copy = optional('.js-mv-copy')
  const chars = copy ? qsa<HTMLElement>('.c-mv-copy-main span', copy) : []
  const sub = copy ? optional('.c-mv-copy-sub', copy) : null

  if (prefersReducedMotion()) {
    if (target) gsap.set(target, { y: 0, opacity: 1, scale: 1 })
    return () => {}
  }

  const tl = gsap.timeline({ paused: true })

  // キャッチコピーを 1 文字ずつ立ち上げ、少し遅れてイラストがせり上がる
  if (chars.length > 0) {
    tl.fromTo(
      chars,
      { yPercent: 110, opacity: 0 },
      { yPercent: 0, opacity: 1, duration: 0.9, ease: 'power3.out', stagger: 0.055 },
      0,
    )
  }

  if (sub) {
    tl.fromTo(sub, { y: 12, opacity: 0 }, { y: 0, opacity: 0.72, duration: 0.7, ease: 'power2.out' }, 0.45)
  }

  if (target) {
    // opacity は動かさない。FV のイラストはページ最大の描画（LCP）なので、
    // opacity: 0 から始めると「LCP ＝ 演出完了後」と計測されてしまう。
    // オーバーレイの裏では描画済みにしておき、位置とスケールだけで登場させる
    tl.fromTo(
      target,
      { y: '8vh', scale: 1.04 },
      { y: 0, scale: 1, duration: 1.4, ease: 'power3.out' },
      0.2,
    )
  }

  return () => {
    tl.play()
  }
}
